import type {Component, Editor} from 'grapesjs';
import {GA4EventName} from '@Libraries/ga-events';
import {getUniqueId} from '@Utils/string.util';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMediaOverlayVariant} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {getAllComponentsByType, isMediaComponentLoading} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {uploadImageFileIntoComponent} from '@EmailMakerReact/lib/email-maker-media-library';
import {trackGaEventForComponent} from '@EmailMakerReact/lib/email-maker-misc-library';
import {
  clearMediaComponentOverlayContent,
  doesMediaComponentHaveUserAsset,
  hideAllMediaComponentOverlays,
  hideMediaComponentOverlay,
  IMAGE_OVERLAY_DROP_TEXT_KEY,
  prepareMediaComponentOverlay,
  showMediaComponentOverlay,
} from '@EmailMakerReact/lib/email-maker-overlay-library';

const IMAGE_MIME_PREFIX = 'image/';
const FILE_DRAG_ITEM_KIND = 'file';
const DRAG_SOURCE_MODEL_KEY = 'dragSource';
const DROPPED_IMAGE_CONTENT_TYPE = 'image';
const GRAPESJS_INSERT_NOTHING_CONTENT = '';
const IMAGE_FILE_DRAG_CLASS = 'image-file-drag';
const IMAGE_FILE_DRAG_MODEL_KEY = 'pmwEmailMakerImageFileDrag';
const PLACEHOLDER_DROP_TARGET_CLASS = 'placeholder-drop-target';
const PLACEMENT_LINE_LABEL_ATTRIBUTE = 'data-drop-label';
const PLACEMENT_LINE_CONTENT_SELECTOR = '.gjs-placeholder-int';

type DropIdentifier = string;

interface ComponentDropContent {
  type: string;
  attributes: Record<string, unknown>;
  file?: File;
}

type CanvasDropContent = ComponentDropContent[] | typeof GRAPESJS_INSERT_NOTHING_CONTENT;

export interface CanvasDropData {
  content: CanvasDropContent;
}

interface CanvasFrameLoadEvent {
  window: Window;
}

export interface InsertionPoint {
  parent: Component;
  index: number;
}

enum ImageDragPhase {
  IDLE = 'idle',
  DRAGGING = 'dragging',
  PLACEHOLDER_HOVERED = 'placeholderHovered',
  DROPPED = 'dropped',
}

export class ImageFileDragDrop {
  readonly editor: Editor;
  readonly filesByDropIdentifier = new Map<DropIdentifier, File>();
  phase = ImageDragPhase.IDLE;
  target: Component | undefined = undefined;
  selectionBeforeDrag: Component | undefined = undefined;
  preparedPlaceholders: Component[] = [];

  constructor(editor: Editor) {
    this.editor = editor;
    this.bindDragEnterBeforeGrapesJs(editor.Canvas.getDocument());
    editor.on('canvas:frame:load', this.onCanvasFrameLoad);
    editor.on('canvas:dragover', this.onCanvasDragOver);
    editor.on('canvas:drop', this.onCanvasDrop);
    editor.on('canvas:dragend', this.onCanvasDragEnd);
  }

  teardown(): void {
    this.cleanUpImageDrag();
    this.editor.Canvas.getDocument()?.removeEventListener('dragenter', this.onCanvasDocumentDragEnter, true);
    this.editor.off('canvas:frame:load', this.onCanvasFrameLoad);
    this.editor.off('canvas:dragover', this.onCanvasDragOver);
    this.editor.off('canvas:drop', this.onCanvasDrop);
    this.editor.off('canvas:dragend', this.onCanvasDragEnd);
  }

  getDropContent(dataTransfer: DataTransfer, content: CanvasDropContent): CanvasDropContent {
    const imageFiles = Array.from(dataTransfer.files).filter(isImageFile);
    if (this.target && imageFiles.length > 0) {
      this.dropImagesIntoPlaceholder(this.target, imageFiles);
      return GRAPESJS_INSERT_NOTHING_CONTENT;
    }

    return this.toImageBlockContent(content);
  }

  bindDragEnterBeforeGrapesJs(canvasDocument: Document | undefined): void {
    canvasDocument?.addEventListener('dragenter', this.onCanvasDocumentDragEnter, true);
  }

  readonly onCanvasFrameLoad = ({window: frameWindow}: CanvasFrameLoadEvent): void => {
    this.bindDragEnterBeforeGrapesJs(frameWindow.document);
  };

  readonly onCanvasDocumentDragEnter = (event: DragEvent): void => {
    if (!this.isFreshDragEnter()) {
      return;
    }

    this.cleanUpStrandedImageDrag();
    this.rememberSelectionBeforeDrag();
    if (hasImageFileItem(event.dataTransfer?.items)) {
      this.startImageDrag();
    }
  };

  readonly onCanvasDragOver = (event: DragEvent): void => {
    const placeholder = this.getEmptyImagePlaceholderUnderDrag(event);
    if (placeholder) {
      this.enterPlaceholder(placeholder);
    } else {
      this.leavePlaceholder();
    }
  };

  readonly onCanvasDrop = (_dataTransfer: DataTransfer, droppedComponent: Component): void => {
    this.phase = ImageDragPhase.DROPPED;
    const droppedImageBlocks = this.findDroppedImageBlocks();
    this.uploadDroppedImageBlocks(droppedImageBlocks);
    this.editor.select(droppedImageBlocks[0] ?? droppedComponent);
  };

  readonly onCanvasDragEnd = (): void => {
    if (this.phase === ImageDragPhase.IDLE) {
      return;
    }

    const shouldRestoreSelection = this.phase === ImageDragPhase.PLACEHOLDER_HOVERED;
    const selectionBeforeDrag = this.selectionBeforeDrag;
    this.cleanUpImageDrag();
    if (shouldRestoreSelection) {
      this.editor.select(selectionBeforeDrag ?? []);
    }
  };

  isFreshDragEnter(): boolean {
    return this.editor.getModel().defaultRunning && !this.target;
  }

  cleanUpStrandedImageDrag(): void {
    if (this.phase !== ImageDragPhase.IDLE) {
      this.cleanUpImageDrag();
    }
  }

  rememberSelectionBeforeDrag(): void {
    this.selectionBeforeDrag = this.editor.getSelected();
  }

  startImageDrag(): void {
    this.phase = ImageDragPhase.DRAGGING;
    this.tellGrapesJsTheDragCarriesAnImageBlock();
    hideStaleHoverOverlays(this.editor);
    this.wrapEmptyPlaceholdersForTheWholeDrag();
    this.flagImageFileDragOnModel();
    this.styleCanvasForImageFileDrag();
  }

  cleanUpImageDrag(): void {
    if (this.phase === ImageDragPhase.IDLE) {
      return;
    }

    this.tellGrapesJsTheDragIsOver();
    this.unwrapPlaceholdersAfterTheDrag();
    this.unflagImageFileDragOnModel();
    this.unstyleCanvasForImageFileDrag();
    this.target = undefined;
    this.selectionBeforeDrag = undefined;
    this.filesByDropIdentifier.clear();
    this.phase = ImageDragPhase.IDLE;
  }

  tellGrapesJsTheDragCarriesAnImageBlock(): void {
    this.editor.getModel().set(DRAG_SOURCE_MODEL_KEY, {content: {type: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE}});
  }

  tellGrapesJsTheDragIsOver(): void {
    this.editor.getModel().unset(DRAG_SOURCE_MODEL_KEY);
  }

  wrapEmptyPlaceholdersForTheWholeDrag(): void {
    this.preparedPlaceholders = getAllImageComponents(this.editor).filter(isEmptyImagePlaceholder);
    for (const placeholder of this.preparedPlaceholders) {
      prepareMediaComponentOverlay(this.editor, placeholder.getEl());
    }
  }

  unwrapPlaceholdersAfterTheDrag(): void {
    for (const placeholder of this.preparedPlaceholders) {
      hideMediaComponentOverlay(this.editor, placeholder.getEl());
    }

    this.preparedPlaceholders = [];
  }

  flagImageFileDragOnModel(): void {
    this.editor.getModel().set(IMAGE_FILE_DRAG_MODEL_KEY, true);
  }

  unflagImageFileDragOnModel(): void {
    this.editor.getModel().unset(IMAGE_FILE_DRAG_MODEL_KEY);
  }

  styleCanvasForImageFileDrag(): void {
    this.editor.getContainer()?.classList.add(IMAGE_FILE_DRAG_CLASS);
    this.getPlacementLineContentElement()?.setAttribute(PLACEMENT_LINE_LABEL_ATTRIBUTE, window.i18next.t(IMAGE_OVERLAY_DROP_TEXT_KEY));
  }

  unstyleCanvasForImageFileDrag(): void {
    this.editor.getContainer()?.classList.remove(IMAGE_FILE_DRAG_CLASS, PLACEHOLDER_DROP_TARGET_CLASS);
    this.getPlacementLineContentElement()?.removeAttribute(PLACEMENT_LINE_LABEL_ATTRIBUTE);
  }

  getPlacementLineContentElement(): HTMLElement | null | undefined {
    return this.editor.Canvas.getPlacerEl()?.querySelector<HTMLElement>(PLACEMENT_LINE_CONTENT_SELECTOR);
  }

  toImageBlockContent(content: CanvasDropContent): CanvasDropContent {
    if (!Array.isArray(content)) {
      return content;
    }

    return content.map((item) => this.toImageBlockWithoutFile(item));
  }

  toImageBlockWithoutFile(item: ComponentDropContent): ComponentDropContent {
    if (item.type !== DROPPED_IMAGE_CONTENT_TYPE || !item.file) {
      return item;
    }

    return {type: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE, attributes: this.toImageBlockAttributes(item.attributes, item.file)};
  }

  toImageBlockAttributes(droppedAttributes: Record<string, unknown>, file: File): Record<string, unknown> {
    const attributes: Record<string, unknown> = {...droppedAttributes, [EMAIL_MAKER_PROPS.DROP_IDENTIFIER]: this.registerDroppedFile(file)};
    delete attributes.alt;
    return attributes;
  }

  registerDroppedFile(file: File): DropIdentifier {
    const identifier = getUniqueId();
    this.filesByDropIdentifier.set(identifier, file);
    return identifier;
  }

  findDroppedImageBlocks(): Component[] {
    if (this.filesByDropIdentifier.size === 0) {
      return [];
    }

    return getAllImageComponents(this.editor).filter((component) => this.filesByDropIdentifier.has(getDropIdentifier(component) ?? ''));
  }

  uploadDroppedImageBlocks(blocks: Component[]): void {
    for (const block of blocks) {
      const file = this.takeDroppedFile(block);
      if (!file) {
        continue;
      }

      this.stripDropIdentifierOutsideUndoHistory(block);
      this.uploadFileIntoImage(block, file);
    }
  }

  takeDroppedFile(block: Component): File | undefined {
    const identifier = getDropIdentifier(block) ?? '';
    const file = this.filesByDropIdentifier.get(identifier);
    this.filesByDropIdentifier.delete(identifier);
    return file;
  }

  stripDropIdentifierOutsideUndoHistory(block: Component): void {
    this.editor.getModel().skip(() => {
      block.removeAttributes(EMAIL_MAKER_PROPS.DROP_IDENTIFIER);
    });
  }

  uploadFileIntoImage(image: Component, file: File): void {
    trackGaEventForComponent(this.editor, GA4EventName.EMAILMAKER_UPLOAD_MEDIA, image);
    uploadImageFileIntoComponent(this.editor, file, image);
  }

  dropImagesIntoPlaceholder(placeholder: Component, files: File[]): void {
    const [firstFile, ...extraFiles] = files;

    this.phase = ImageDragPhase.DROPPED;
    this.target = undefined;
    this.fillPlaceholder(placeholder, firstFile);
    this.insertImageBlocksAfter(placeholder, extraFiles);
    this.uploadDroppedImageBlocks(this.findDroppedImageBlocks());
  }

  fillPlaceholder(placeholder: Component, file: File): void {
    clearMediaComponentOverlayContent(this.editor, placeholder.getEl());
    this.editor.select(placeholder);
    this.uploadFileIntoImage(placeholder, file);
  }

  insertImageBlocksAfter(sibling: Component, files: File[]): void {
    const insertionPoint = getInsertionPointAfter(sibling);
    if (!insertionPoint || files.length === 0) {
      return;
    }

    const blockDefinitions = files.map((file) => {
      return {type: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE, attributes: {[EMAIL_MAKER_PROPS.DROP_IDENTIFIER]: this.registerDroppedFile(file)}};
    });

    insertionPoint.parent.components().add(blockDefinitions, {at: insertionPoint.index});
  }

  enterPlaceholder(placeholder: Component): void {
    if (this.target === placeholder) {
      return;
    }

    this.leavePlaceholder();
    this.target = placeholder;
    this.phase = ImageDragPhase.PLACEHOLDER_HOVERED;
    this.showDropOverlayOnPlaceholder(placeholder);
    this.restoreGrapesJsTools(placeholder);
  }

  leavePlaceholder(): void {
    const target = this.target;
    if (!target) {
      return;
    }

    this.hideDropOverlayOnPlaceholder(target);
    this.target = undefined;
    this.editor.getModel().stopDefault();
  }

  showDropOverlayOnPlaceholder(placeholder: Component): void {
    showMediaComponentOverlay(this.editor, placeholder.getEl(), EmailMediaOverlayVariant.DROP);
    this.editor.getContainer()?.classList.add(PLACEHOLDER_DROP_TARGET_CLASS);
  }

  hideDropOverlayOnPlaceholder(placeholder: Component): void {
    clearMediaComponentOverlayContent(this.editor, placeholder.getEl());
    this.editor.getContainer()?.classList.remove(PLACEHOLDER_DROP_TARGET_CLASS);
  }

  restoreGrapesJsTools(placeholder: Component): void {
    this.editor.select(placeholder);
    this.editor.getModel().runDefault({preserveSelected: true});
  }

  getEmptyImagePlaceholderUnderDrag(event: DragEvent): Component | undefined {
    const imageElement = getImageElementUnderDrag(event);
    if (!imageElement) {
      return undefined;
    }

    const component = this.editor.Components.getById(imageElement.id);
    return component && isEmptyImagePlaceholder(component) ? component : undefined;
  }
}

export function isImageFileDragActive(editor: Editor): boolean {
  return editor.getModel().get(IMAGE_FILE_DRAG_MODEL_KEY) === true;
}

function hideStaleHoverOverlays(editor: Editor): void {
  hideAllMediaComponentOverlays(editor);
}

export function getInsertionPointAfter(sibling: Component): InsertionPoint | undefined {
  const parent = sibling.parent();
  if (!parent) {
    return undefined;
  }

  if (parent.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_LINK)) {
    return getInsertionPointAfter(parent);
  }

  return {parent, index: sibling.index() + 1};
}

export function isImageFile(file: Pick<File, 'type'>): boolean {
  return isImageMimeType(file.type);
}

export function hasImageFileItem(items: ArrayLike<Pick<DataTransferItem, 'kind' | 'type'>> | undefined): boolean {
  if (!items) {
    return false;
  }

  return Array.from(items).some((item) => item.kind === FILE_DRAG_ITEM_KIND && isImageMimeType(item.type));
}

function isImageMimeType(mimeType: string): boolean {
  return mimeType.startsWith(IMAGE_MIME_PREFIX);
}

function getImageElementUnderDrag(event: DragEvent): HTMLElement | undefined {
  const {target} = event;
  if (!hasImageFileItem(event.dataTransfer?.items) || !(target instanceof HTMLElement) || !target.hasAttribute(EMAIL_MAKER_PROPS.IS_PMW_IMAGE)) {
    return undefined;
  }

  return target;
}

function getAllImageComponents(editor: Editor): Component[] {
  return getAllComponentsByType(editor, [EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE], editor.getWrapper());
}

function getDropIdentifier(component: Component): DropIdentifier | undefined {
  const identifier: unknown = component.getAttributes()[EMAIL_MAKER_PROPS.DROP_IDENTIFIER];
  return typeof identifier === 'string' ? identifier : undefined;
}

function isEmptyImagePlaceholder(component: Component): boolean {
  return component.get('type') === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE && !doesMediaComponentHaveUserAsset(component) && !isMediaComponentLoading(component);
}
